import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useNavigate, type NavigateOptions } from "react-router-dom";
import { DiscardChangesDialog } from "@playbookai/ui";

/* Unsaved-changes guard for the Settings host (PB-5134, t09).
 *
 * Draft pages register their dirty flag; every navigation the host itself
 * performs — rail items, sub-tabs, crumbs, in-page pointers — goes through
 * `useGuardedNavigate`, which asks with the design-system discard dialog
 * while anything is dirty, and `beforeunload` is armed for a reload or a
 * closed tab. The shell runs a plain BrowserRouter, so history-driven moves
 * (Back, an app tab in the top bar) are outside this guard by design. */

interface LeaveGuardContextValue {
  readonly register: (id: string, dirty: boolean) => void;
  readonly unregister: (id: string) => void;
  readonly confirmLeave: () => Promise<boolean>;
}

const LeaveGuardContext = createContext<LeaveGuardContextValue | null>(null);

export function SettingsLeaveGuardProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const dirtyIds = useRef(new Set<string>());
  const [pending, setPending] = useState<((leave: boolean) => void) | null>(
    null,
  );

  const register = useCallback((id: string, dirty: boolean) => {
    if (dirty) dirtyIds.current.add(id);
    else dirtyIds.current.delete(id);
  }, []);
  const unregister = useCallback((id: string) => {
    dirtyIds.current.delete(id);
  }, []);
  const confirmLeave = useCallback(() => {
    if (dirtyIds.current.size === 0) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setPending(() => resolve));
  }, []);
  const settle = (leave: boolean): void => {
    pending?.(leave);
    setPending(null);
    if (leave) dirtyIds.current.clear();
  };

  useEffect(() => {
    const handler = (event: BeforeUnloadEvent): void => {
      if (dirtyIds.current.size === 0) return;
      event.preventDefault();
      // Legacy browsers read the returnValue; the text itself is ignored.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const value = useMemo<LeaveGuardContextValue>(
    () => ({ register, unregister, confirmLeave }),
    [register, unregister, confirmLeave],
  );

  return (
    <LeaveGuardContext.Provider value={value}>
      {children}
      <DiscardChangesDialog
        open={pending !== null}
        onKeepEditing={() => settle(false)}
        onDiscard={() => settle(true)}
      />
    </LeaveGuardContext.Provider>
  );
}

/** Report this page's dirty state to the host guard while mounted. */
export function useLeaveGuard(dirty: boolean): void {
  const guard = useContext(LeaveGuardContext);
  const id = useId();
  useEffect(() => {
    guard?.register(id, dirty);
    return () => guard?.unregister(id);
  }, [guard, id, dirty]);
}

/** Ask before an exit that is NOT a navigation drops a draft — switching a
 * sub-tab inside one page, say, where nothing routes and the panel simply
 * unmounts. Resolves true at once when nothing is dirty, and outside the host,
 * so a caller can treat it as a plain gate. */
export function useConfirmLeave(): () => Promise<boolean> {
  const guard = useContext(LeaveGuardContext);
  return useCallback(
    () => guard?.confirmLeave() ?? Promise.resolve(true),
    [guard],
  );
}

/** `navigate` that asks first while a draft is dirty; plain `navigate` outside the host. */
export function useGuardedNavigate(): (
  to: string,
  options?: NavigateOptions,
) => void {
  const navigate = useNavigate();
  const guard = useContext(LeaveGuardContext);
  return useCallback(
    (to: string, options?: NavigateOptions) => {
      if (!guard) {
        void navigate(to, options);
        return;
      }
      void guard.confirmLeave().then((leave) => {
        if (leave) void navigate(to, options);
      });
    },
    [guard, navigate],
  );
}
